import { useMemo } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import { loadState } from "library/helpers/sessionStorage";

type UseUserAssignedVendorsOptions = {
  includeAllOption?: boolean;
};

type DropdownOption = {
  label: string;
  value: string;
};

function selectVendorsArray(state: RootState): any[] {
  const allCustomersVendorsVendors: any = (state as any)?.allCustomersVendors
    ?.vendors;
  if (Array.isArray(allCustomersVendorsVendors)) {
    return allCustomersVendorsVendors;
  }
  if (
    allCustomersVendorsVendors?.vendors &&
    Array.isArray(allCustomersVendorsVendors.vendors)
  ) {
    return allCustomersVendorsVendors.vendors;
  }
  const vendorsNode: any = (state as any)?.vendors;
  if (Array.isArray(vendorsNode)) {
    return vendorsNode;
  }
  if (vendorsNode?.vendors && Array.isArray(vendorsNode.vendors)) {
    return vendorsNode.vendors;
  }
  return [];
}

export const useUserAssignedVendors = (
  options: UseUserAssignedVendorsOptions = {}
) => {
  const { includeAllOption = false } = options;
  const vendors = useSelector((state: RootState) => selectVendorsArray(state));
  const userDetails: any = loadState("userInfo");

  const filteredVendors = useMemo(() => {
    if (!vendors || vendors.length === 0) return [] as any[];
    const userAssignedVendors: any[] = userDetails?.vendors || [];
    if (!userAssignedVendors.length) return [] as any[];

    return vendors.filter((vendor: any) =>
      userAssignedVendors.some((userVendor: any) => {
        const matchById =
          (userVendor?.id &&
            (userVendor.id === vendor?.id ||
              userVendor.id === vendor?.vendorId)) ||
          false;
        const matchByVendorId =
          (userVendor?.vendorId && userVendor.vendorId === vendor?.vendorId) ||
          false;
        const matchByName =
          (userVendor?.name && userVendor.name === vendor?.vendorId) || false;
        return matchById || matchByVendorId || matchByName;
      })
    );
  }, [vendors, userDetails]);

  const vendorDropdownOptions: DropdownOption[] = useMemo(() => {
    const base = filteredVendors.map((vendor: any) => ({
      label: vendor?.vendorName,
      value: vendor?.vendorId,
    }));
    if (includeAllOption) {
      return [{ label: "ALL", value: "ALL" }, ...base];
    }
    return base;
  }, [filteredVendors, includeAllOption]);

  const vendorCodes: string[] = useMemo(
    () =>
      filteredVendors.map((vendor: any) => vendor?.vendorId).filter(Boolean),
    [filteredVendors]
  );

  return {
    filteredVendors,
    vendorDropdownOptions,
    vendorCodes,
  };
};

export default useUserAssignedVendors;
